import { matchPath, useLocation, useMatch, type RouteObject } from "react-router";
import type { Routes } from "..";

// Utility type to remove double slashes and trailing slashes (except for root '/')
export type CleanPath<T extends string> = T extends `${infer Rest}//${infer After}`
  ? CleanPath<`${Rest}/${After}`> // Remove double slashes
  : T extends "/"
    ? T // Keep root '/'
    : T extends `${infer Rest}/`
      ? CleanPath<Rest> // Remove trailing slash recursively
      : T;

// Utility type to join two path segments and clean the result
export type JoinAndCleanPaths<BasePath extends string, Path extends string> = CleanPath<
  BasePath extends "/"
    ? Path extends "" // Handle empty path segment when base is root
      ? "/"
      : `/${Path}`
    : Path extends "" // Handle empty path segment when base is not root
      ? BasePath // Return base path unchanged
      : `${BasePath}/${Path}`
>;

// Recursive type to extract all possible path strings from a route configuration array
export type ExtractPaths<
  // Use ReadonlyArray<AppRouteObject> to match the structure, especially if using `as const`
  TRoutes extends readonly RouteObject[],
  TBasePath extends string = "/", // Start with root path '/'
> = {
  // Iterate over each route object in the input array
  [K in keyof TRoutes]: TRoutes[K] extends infer R
    ? R extends RouteObject // Ensure R is a valid route object
      ? // Determine the full path for the current route R
        | (R extends { path: infer P extends string } // Case 1: Route has a 'path' property
              ? JoinAndCleanPaths<TBasePath, P> // Join base path with the route's path and clean
              : R extends { index: true } // Case 2: Route has 'index: true'
                ? CleanPath<TBasePath> // Index route's path is the cleaned base path
                : CleanPath<TBasePath>) // Case 3: Layout Route (no path/index) - Path is the cleaned base path
          // Recursively extract paths from children, if they exist
          | (R extends { children: infer C extends readonly RouteObject[] }
              ? ExtractPaths<
                  C,
                  // Determine the base path for the children
                  R extends { path: infer P extends string } // If parent had a path...
                    ? JoinAndCleanPaths<TBasePath, P> // ...use its cleaned full path
                    : CleanPath<TBasePath> // If parent was index/layout, children inherit cleaned base path
                >
              : never) // No children, no recursive paths
      : never // Should not happen if TRoutes is well-typed
    : never;
}[number]; // Use [number] to get a union of all calculated paths from the iteration

type ExtractParam<Path, NextPart> = Path extends `:${infer Param}`
  ? { [key in Param]: string } & NextPart
  : NextPart;

export type ExtractParams<Path> = Path extends `${infer Segment}/${infer Rest}`
  ? ExtractParam<Segment, ExtractParams<Rest>>
  : ExtractParam<Path, object>;

export type Paths = ExtractPaths<Routes>;

export const toPath = <P extends Paths>(path: P, params: ExtractParams<P>): string => {
  const result = path.replace(/:([\w-]+)(\?)?/g, (substring: string, paramName: string) => {
    if (Object.hasOwn(params, paramName))
      return (params as { [key: string]: string })[paramName] ?? substring;
    return substring;
  });

  return result;
};

export const fromHostBasename = (path: string) =>
  `${window.location.protocol}//${window.location.host}${document.querySelector("base")?.href ?? ""}${path}`;

export const useMatchPattern = <P extends Paths>(pattern: P) => useMatch(pattern);

export const matchRouterPath = (pattern: Paths, path: string) => matchPath(pattern, path);
/**
 * Check if the current location matches any of the given paths.
 * @param paths - An array of paths or a single path to check against the current location.
 * @returns boolean - True if the current location matches any of the given paths, false otherwise.
 */
export const useIsCurrentRouteMatching = (paths: Paths | Paths[]): boolean => {
  const location = useLocation();

  if (typeof paths === "string") {
    paths = [paths];
  }
  return paths.some(path => matchPath(path, location.pathname));
};
